import { Cog, Drill, Monitor, PencilRuler, type LucideIcon } from 'lucide-react';
import type { BadgeProps } from '@/components/ui/Badge';
import { addDaysIsoDate, todayIsoDate } from '@/lib/formatters';
import { canWrite, type Role } from '@/lib/permissions';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

// ---------------------------------------------------------------------------
// Categories — one sidebar sub-row each. Must match the
// `equipment_category_check` constraint in migration 134.
// ---------------------------------------------------------------------------

export const EQUIPMENT_CATEGORIES = ['office', 'prototype', 'shop', 'injection'] as const;
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number];

export interface EquipmentCategoryMeta {
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  /** Asset-tag prefix used when suggesting the next tag, e.g. SHP-014. */
  tagPrefix: string;
  /** Default useful life for new records in this category. */
  defaultLifeYears: number;
  typeSuggestions: string[];
  specSuggestions: string[];
}

export const EQUIPMENT_CATEGORY_META: Record<EquipmentCategory, EquipmentCategoryMeta> = {
  office: {
    label: 'Office equipment',
    shortLabel: 'Office',
    icon: Monitor,
    tagPrefix: 'OFC',
    defaultLifeYears: 5,
    typeSuggestions: [
      'Laptop',
      'Desktop computer',
      'Monitor',
      'Printer / scanner',
      'Phone',
      'Network equipment',
      'Projector / display',
      'Furniture',
    ],
    specSuggestions: ['Processor', 'Memory', 'Storage', 'Operating system', 'Hostname', 'MAC address'],
  },
  prototype: {
    label: 'Prototype equipment',
    shortLabel: 'Prototype',
    icon: PencilRuler,
    tagPrefix: 'PRT',
    defaultLifeYears: 5,
    typeSuggestions: [
      '3D printer (FDM)',
      '3D printer (resin)',
      'Laser cutter',
      'Desktop CNC',
      'Vacuum former',
      '3D scanner',
      'Test fixture',
      'Measuring instrument',
    ],
    specSuggestions: ['Build volume', 'Materials', 'Nozzle size', 'Laser power', 'Bed size', 'Resolution'],
  },
  shop: {
    label: 'Shop equipment',
    shortLabel: 'Shop',
    icon: Drill,
    tagPrefix: 'SHP',
    defaultLifeYears: 7,
    typeSuggestions: [
      'CNC mill',
      'Lathe',
      'Drill press',
      'Band saw',
      'Welder',
      'Air compressor',
      'Forklift / pallet jack',
      'Power tool',
      'Measuring instrument',
    ],
    specSuggestions: ['Voltage', 'Phase', 'Amperage', 'Air pressure', 'Footprint', 'Weight'],
  },
  injection: {
    label: 'Injection equipment',
    shortLabel: 'Injection',
    icon: Cog,
    tagPrefix: 'INJ',
    defaultLifeYears: 7,
    typeSuggestions: [
      'Injection molding machine',
      'Mold',
      'Resin dryer',
      'Chiller',
      'Mold temperature controller',
      'Sprue picker / robot',
      'Granulator',
      'Conveyor',
    ],
    specSuggestions: [
      'Clamp tonnage',
      'Shot size',
      'Screw diameter',
      'Tie bar spacing',
      'Max mold height',
      'Voltage',
    ],
  },
};

export function isEquipmentCategory(value: string | null | undefined): value is EquipmentCategory {
  return !!value && (EQUIPMENT_CATEGORIES as readonly string[]).includes(value);
}

export function equipmentCategoryLabel(value: string): string {
  return isEquipmentCategory(value) ? EQUIPMENT_CATEGORY_META[value].label : humanize(value);
}

export const EQUIPMENT_CATEGORY_OPTIONS = EQUIPMENT_CATEGORIES.map((value) => ({
  value,
  label: EQUIPMENT_CATEGORY_META[value].label,
}));

/** Next free tag for a category: one past the highest `PREFIX-n` in use. */
export function suggestAssetTag(category: EquipmentCategory, existingTags: string[]): string {
  const prefix = EQUIPMENT_CATEGORY_META[category].tagPrefix;
  const pattern = new RegExp(`^${prefix}-(\\d+)$`, 'i');
  let max = 0;
  for (const tag of existingTags) {
    const match = pattern.exec(tag.trim());
    if (match) max = Math.max(max, Number(match[1]));
  }
  return `${prefix}-${String(max + 1).padStart(3, '0')}`;
}

// ---------------------------------------------------------------------------
// Status / condition
// ---------------------------------------------------------------------------

export const EQUIPMENT_STATUSES = [
  'in_service',
  'under_maintenance',
  'out_of_service',
  'in_storage',
  'retired',
] as const;
export type EquipmentStatus = (typeof EQUIPMENT_STATUSES)[number];

const EQUIPMENT_STATUS_META: Record<EquipmentStatus, { label: string; variant: BadgeVariant }> = {
  in_service: { label: 'In service', variant: 'success' },
  under_maintenance: { label: 'Under maintenance', variant: 'info' },
  out_of_service: { label: 'Out of service', variant: 'warning' },
  in_storage: { label: 'In storage', variant: 'muted' },
  retired: { label: 'Retired', variant: 'muted' },
};

/** Unknown values render as a muted badge rather than breaking the grid. */
export function getEquipmentStatusMeta(status: string): { label: string; variant: BadgeVariant } {
  return (
    EQUIPMENT_STATUS_META[status as EquipmentStatus] ?? { label: humanize(status), variant: 'muted' }
  );
}

export const EQUIPMENT_STATUS_OPTIONS = EQUIPMENT_STATUSES.map((value) => ({
  value,
  label: EQUIPMENT_STATUS_META[value].label,
}));

export const EQUIPMENT_CONDITIONS = ['new', 'excellent', 'good', 'fair', 'poor'] as const;
export type EquipmentCondition = (typeof EQUIPMENT_CONDITIONS)[number];

const EQUIPMENT_CONDITION_META: Record<EquipmentCondition, { label: string; variant: BadgeVariant }> = {
  new: { label: 'New', variant: 'success-soft' },
  excellent: { label: 'Excellent', variant: 'success-soft' },
  good: { label: 'Good', variant: 'info-soft' },
  fair: { label: 'Fair', variant: 'warning-soft' },
  poor: { label: 'Poor', variant: 'warning' },
};

export function getEquipmentConditionMeta(
  condition: string | null,
): { label: string; variant: BadgeVariant } | null {
  if (!condition) return null;
  return (
    EQUIPMENT_CONDITION_META[condition as EquipmentCondition] ?? {
      label: humanize(condition),
      variant: 'muted',
    }
  );
}

export const EQUIPMENT_CONDITION_OPTIONS = EQUIPMENT_CONDITIONS.map((value) => ({
  value,
  label: EQUIPMENT_CONDITION_META[value].label,
}));

// ---------------------------------------------------------------------------
// Depreciation methods
// ---------------------------------------------------------------------------

export const DEPRECIATION_METHODS = [
  'straight_line',
  'declining_balance',
  'sum_of_years_digits',
  'macrs',
  'expensed',
  'none',
] as const;
export type DepreciationMethod = (typeof DEPRECIATION_METHODS)[number];

export const DEPRECIATION_METHOD_META: Record<DepreciationMethod, { label: string; hint: string }> = {
  straight_line: {
    label: 'Straight-line',
    hint: 'Equal expense each year down to salvage value.',
  },
  declining_balance: {
    label: 'Double-declining balance',
    hint: 'Front-loaded at 2× the straight-line rate, switching to straight-line when larger.',
  },
  sum_of_years_digits: {
    label: "Sum-of-years' digits",
    hint: 'Front-loaded, declining by a fixed fraction each year.',
  },
  macrs: {
    label: 'MACRS (GDS, half-year)',
    hint: 'IRS tax tables for 3, 5, 7, 10, 15 or 20-year property. Ignores salvage.',
  },
  expensed: {
    label: 'Expensed (Section 179 / bonus)',
    hint: 'Full cost written off in the year placed in service.',
  },
  none: {
    label: 'Not depreciated',
    hint: 'Tracked at cost — e.g. under the de minimis threshold.',
  },
};

export const DEPRECIATION_METHOD_OPTIONS = DEPRECIATION_METHODS.map((value) => ({
  value,
  label: DEPRECIATION_METHOD_META[value].label,
  hint: DEPRECIATION_METHOD_META[value].hint,
}));

export const MACRS_CLASSES = [3, 5, 7, 10, 15, 20] as const;

export function isDepreciationMethod(value: string): value is DepreciationMethod {
  return (DEPRECIATION_METHODS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Disposal
// ---------------------------------------------------------------------------

export const DISPOSAL_METHODS = ['sold', 'scrapped', 'donated', 'traded_in', 'lost', 'other'] as const;
export type DisposalMethod = (typeof DISPOSAL_METHODS)[number];

export const DISPOSAL_METHOD_LABELS: Record<DisposalMethod, string> = {
  sold: 'Sold',
  scrapped: 'Scrapped',
  donated: 'Donated',
  traded_in: 'Traded in',
  lost: 'Lost / stolen',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

export const EQUIPMENT_FILES_BUCKET = 'equipment-files';
export const MAX_EQUIPMENT_FILE_BYTES = 100 * 1024 * 1024;
export const MAX_EQUIPMENT_PHOTO_BYTES = 15 * 1024 * 1024;

export const EQUIPMENT_FILE_KINDS = [
  'photo',
  'manual',
  'warranty',
  'invoice',
  'certificate',
  'sop',
  'drawing',
  'safety',
  'other',
] as const;
export type EquipmentFileKind = (typeof EQUIPMENT_FILE_KINDS)[number];

export const EQUIPMENT_FILE_KIND_LABELS: Record<EquipmentFileKind, string> = {
  photo: 'Photo',
  manual: 'Manual',
  warranty: 'Warranty',
  invoice: 'Invoice / receipt',
  certificate: 'Certificate',
  sop: 'SOP / work instruction',
  drawing: 'Drawing / schematic',
  safety: 'Safety (SDS, LOTO)',
  other: 'Other',
};

/** Kinds offered on the Documents tab — photos have their own tab. */
export const EQUIPMENT_DOCUMENT_KINDS = EQUIPMENT_FILE_KINDS.filter(
  (k): k is Exclude<EquipmentFileKind, 'photo'> => k !== 'photo',
);

// ---------------------------------------------------------------------------
// Maintenance
// ---------------------------------------------------------------------------

export const MAINTENANCE_KINDS = [
  'preventive',
  'repair',
  'inspection',
  'calibration',
  'cleaning',
  'upgrade',
  'other',
] as const;
export type MaintenanceKind = (typeof MAINTENANCE_KINDS)[number];

export const MAINTENANCE_KIND_META: Record<MaintenanceKind, { label: string; variant: BadgeVariant }> = {
  preventive: { label: 'Preventive', variant: 'success-soft' },
  repair: { label: 'Repair', variant: 'warning-soft' },
  inspection: { label: 'Inspection', variant: 'info-soft' },
  calibration: { label: 'Calibration', variant: 'info-soft' },
  cleaning: { label: 'Cleaning', variant: 'outline' },
  upgrade: { label: 'Upgrade', variant: 'outline' },
  other: { label: 'Other', variant: 'muted' },
};

export function getMaintenanceKindMeta(kind: string): { label: string; variant: BadgeVariant } {
  return MAINTENANCE_KIND_META[kind as MaintenanceKind] ?? { label: humanize(kind), variant: 'muted' };
}

export const MAINTENANCE_INTERVAL_UNITS = ['day', 'week', 'month', 'year'] as const;
export type MaintenanceIntervalUnit = (typeof MAINTENANCE_INTERVAL_UNITS)[number];

export function formatInterval(value: number, unit: string): string {
  const noun = value === 1 ? unit : `${unit}s`;
  return value === 1 ? `Every ${noun}` : `Every ${value} ${noun}`;
}

/** Mirrors `public.equipment_plan_next_due` so dialogs can preview it. */
export function nextDueFrom(from: string, value: number, unit: MaintenanceIntervalUnit): string {
  if (unit === 'day') return addDaysIsoDate(from, value);
  if (unit === 'week') return addDaysIsoDate(from, value * 7);
  const [y, m, d] = from.slice(0, 10).split('-').map(Number);
  const months = unit === 'month' ? value : value * 12;
  const target = new Date(y, m - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const dd = String(target.getDate()).padStart(2, '0');
  return `${target.getFullYear()}-${mm}-${dd}`;
}

export type DueState = 'none' | 'overdue' | 'due_soon' | 'scheduled';

export const DUE_SOON_DAYS = 14;

export function dueState(nextDueOn: string | null | undefined): DueState {
  if (!nextDueOn) return 'none';
  const day = nextDueOn.slice(0, 10);
  const today = todayIsoDate();
  if (day < today) return 'overdue';
  if (day <= addDaysIsoDate(today, DUE_SOON_DAYS)) return 'due_soon';
  return 'scheduled';
}

export type WarrantyState = 'none' | 'active' | 'expiring' | 'expired';

export function warrantyState(expiresOn: string | null | undefined): WarrantyState {
  if (!expiresOn) return 'none';
  const day = expiresOn.slice(0, 10);
  const today = todayIsoDate();
  if (day < today) return 'expired';
  if (day <= addDaysIsoDate(today, 60)) return 'expiring';
  return 'active';
}

// ---------------------------------------------------------------------------
// Permissions — mirror the RLS in migration 134.
// ---------------------------------------------------------------------------

/** Equipment record, photos and documents: super_admin / admin. */
export function canEditEquipment(role: Role | null): boolean {
  return canWrite(role, 'catalog');
}

/** Maintenance schedules and log entries: managers too. */
export function canLogEquipmentMaintenance(role: Role | null): boolean {
  return canEditEquipment(role) || role === 'manager';
}

function humanize(value: string): string {
  const words = value.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown';
}
